import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import {
  downloadBlob,
  filenameFromContentDisposition,
} from "../../utils/fileDownload";

import { useAdminScope } from "../../context/AdminScopeContext";
import {
  useWorkspaceNavigation,
} from "../../pages/admin/workspace_engine";
import { AdminButton } from "../../pages/admin/action_engine";
import { usePanelActions } from "../../pages/admin/panel_engine/consumer";
import {
  AdminEmptyState,
  AdminErrorState,
  AdminLoadingState,
  AdminSuccessState,
} from "../../pages/admin/ui_engine";
import {
  approvePurchaseOrder,
  dispatchPurchaseOrder,
  exportPurchaseOrder,
  getPurchaseOrdersErrorMessage,
  listPurchaseOrders,
} from "../../services/purchaseOrdersAdminApi";


export const PURCHASE_ORDERS_ADMIN_PANEL_STATUS =
  Object.freeze({
    LOADING: "loading",
    EMPTY: "empty",
    ERROR: "error",
    READY: "ready",
  });


const STATUS_LABELS = Object.freeze({
  draft: "Brouillon",
  submitted: "Soumise",
  approved: "Approuvée",
  ready_to_dispatch: "Prête à envoyer",
  dispatching: "Envoi en cours",
  dispatched: "Envoyée",
  acknowledged: "Accusée réception",
  partially_received: "Partiellement réceptionnée",
  received: "Réceptionnée",
  closed: "Clôturée",
  dispatch_failed: "Échec d’envoi",
  rejected: "Rejetée",
  cancelled: "Annulée",
});


function normalizePurchaseOrdersResponse(payload) {
  return Array.isArray(payload?.purchase_orders)
    ? payload.purchase_orders
    : [];
}


function displayValue(value, fallback = "—") {
  if (
    value === null
    || value === undefined
    || value === ""
  ) {
    return fallback;
  }

  return String(value);
}


function numericValue(value) {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : 0;
}


function formatQuantity(value) {
  const quantity = Number(value);

  if (!Number.isFinite(quantity)) {
    return "—";
  }

  return new Intl.NumberFormat(
    "fr-FR",
    {
      maximumFractionDigits: 3,
    },
  ).format(quantity);
}


function formatAmount(value, currency = "EUR") {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "—";
  }

  return new Intl.NumberFormat(
    "fr-FR",
    {
      style: "currency",
      currency: (
        typeof currency === "string"
        && currency.trim()
      )
        ? currency.trim()
        : "EUR",
    },
  ).format(amount);
}


function statusLabel(status) {
  return STATUS_LABELS[status] || displayValue(status);
}


function orderLines(order) {
  return Array.isArray(order?.lines)
    ? order.lines
    : [];
}


function orderQuantity(order) {
  return orderLines(order).reduce(
    (total, line) => (
      total + numericValue(line?.quantity)
    ),
    0,
  );
}


function orderKey(order, index) {
  return (
    order?.order_reference
    || `${order?.supplier_uuid || "supplier"}-${index}`
  );
}


function lineKey(line, index) {
  return (
    line?.line_reference
    || line?.supplier_reference
    || `purchase-order-line-${index}`
  );
}


const PURCHASE_ORDER_DISPATCH_CONNECTORS =
  Object.freeze({
    api: {
      label: "API fournisseur",
      formats: ["json"],
      defaultFormat: "json",
      destinationRequired: false,
      destinationLabel: "URL ou destination API",
    },
    csv: {
      label: "Fichier CSV",
      formats: ["csv"],
      defaultFormat: "csv",
      destinationRequired: false,
      destinationLabel: "Destination du fichier",
    },
    email: {
      label: "E-mail",
      formats: ["csv", "txt", "xlsx"],
      defaultFormat: "txt",
      destinationRequired: true,
      destinationLabel: "Adresse e-mail",
    },
    sftp: {
      label: "SFTP",
      formats: ["csv", "txt", "xlsx"],
      defaultFormat: "csv",
      destinationRequired: true,
      destinationLabel: "Destination SFTP",
    },
  });


const PURCHASE_ORDER_FORMAT_LABELS =
  Object.freeze({
    json: "JSON",
    csv: "CSV",
    txt: "TXT",
    xlsx: "XLSX",
  });




function purchaseOrderExportFilename(
  orderReference,
  formatKey,
) {
  const reference = (
    typeof orderReference === "string"
    && orderReference.trim()
  )
    ? orderReference.trim()
    : "purchase-order";

  return `${reference}.${formatKey}`;
}


function dispatchConnectorContract(connectorKey) {
  return (
    PURCHASE_ORDER_DISPATCH_CONNECTORS[
      connectorKey
    ]
    || PURCHASE_ORDER_DISPATCH_CONNECTORS.api
  );
}


function createDispatchIdempotencyKey(
  orderReference,
  connectorKey,
) {
  const randomPart =
    globalThis.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random()
      .toString(16)
      .slice(2)}`;

  return [
    "purchase-order",
    orderReference,
    connectorKey,
    randomPart,
  ].join(":");
}


export default function SupplierPurchaseOrdersAdminPanel() {
  const {
    selectedCompanyUuid,
  } = useAdminScope();

  const {
    panelContext,
  } = useWorkspaceNavigation();

  const initialOrderReference =
    typeof panelContext?.initialOrderReference
      === "string"
      ? panelContext.initialOrderReference.trim()
      : "";

  const [status, setStatus] = useState(
    PURCHASE_ORDERS_ADMIN_PANEL_STATUS.LOADING,
  );
  const [orders, setOrders] = useState([]);
  const [
    selectedOrderReference,
    setSelectedOrderReference,
  ] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [approving, setApproving] = useState(false);
  const approvalInFlightRef = useRef(false);
  const [
    approvalErrorMessage,
    setApprovalErrorMessage,
  ] = useState("");
  const [
    approvedOrder,
    setApprovedOrder,
  ] = useState(null);

  const [dispatchFormOpen, setDispatchFormOpen] =
    useState(false);
  const [dispatching, setDispatching] = useState(false);
  const dispatchInFlightRef = useRef(false);
  const [dispatchConnectorKey, setDispatchConnectorKey] =
    useState("api");
  const [dispatchFormatKey, setDispatchFormatKey] =
    useState("json");
  const [dispatchDestination, setDispatchDestination] =
    useState("");
  const [
    dispatchErrorMessage,
    setDispatchErrorMessage,
  ] = useState("");
  const [
    dispatchResult,
    setDispatchResult,
  ] = useState(null);

  const [exportFormOpen, setExportFormOpen] =
    useState(false);
  const [exporting, setExporting] = useState(false);
  const exportInFlightRef = useRef(false);
  const [exportFormatKey, setExportFormatKey] =
    useState("xlsx");
  const [
    exportErrorMessage,
    setExportErrorMessage,
  ] = useState("");
  const [
    exportSuccess,
    setExportSuccess,
  ] = useState(null);


  const loadOrders = useCallback(
    async ({
      preserveData = false,
    } = {}) => {
      if (preserveData) {
        setRefreshing(true);
      } else {
        setStatus(
          PURCHASE_ORDERS_ADMIN_PANEL_STATUS.LOADING,
        );
      }

      setErrorMessage("");

      try {
        const payload = await listPurchaseOrders({
          companyUuid: selectedCompanyUuid,
        });

        const normalizedOrders =
          normalizePurchaseOrdersResponse(payload);

        setOrders(normalizedOrders);

        setSelectedOrderReference((current) => {
          if (!normalizedOrders.length) {
            return "";
          }

          const initialExists =
            initialOrderReference
            && normalizedOrders.some(
              (order) => (
                order?.order_reference
                === initialOrderReference
              ),
            );

          if (initialExists) {
            return initialOrderReference;
          }

          const preserved = normalizedOrders.some(
            (order) => (
              order?.order_reference === current
            ),
          );

          return preserved
            ? current
            : normalizedOrders[0]?.order_reference || "";
        });

        setStatus(
          normalizedOrders.length
            ? PURCHASE_ORDERS_ADMIN_PANEL_STATUS.READY
            : PURCHASE_ORDERS_ADMIN_PANEL_STATUS.EMPTY,
        );
      } catch (error) {
        if (!preserveData) {
          setOrders([]);
          setSelectedOrderReference("");
          setStatus(
            PURCHASE_ORDERS_ADMIN_PANEL_STATUS.ERROR,
          );
        }

        setErrorMessage(
          getPurchaseOrdersErrorMessage(
            error,
            "Impossible de charger les commandes fournisseurs.",
          ),
        );
      } finally {
        setRefreshing(false);
      }
    },
    [
      initialOrderReference,
      selectedCompanyUuid,
    ],
  );


  useEffect(() => {
    loadOrders();
  }, [
    loadOrders,
  ]);


  const refresh = useCallback(
    () => loadOrders({
      preserveData: orders.length > 0,
    }),
    [
      loadOrders,
      orders.length,
    ],
  );


  const selectedOrder = useMemo(
    () => orders.find(
      (order) => (
        order?.order_reference
        === selectedOrderReference
      ),
    ) || null,
    [
      orders,
      selectedOrderReference,
    ],
  );


  const activeDispatchConnector = useMemo(
    () => dispatchConnectorContract(
      dispatchConnectorKey,
    ),
    [
      dispatchConnectorKey,
    ],
  );


  const availableDispatchFormats =
    activeDispatchConnector.formats;


  const approveSelectedOrder = useCallback(
    async () => {
      const orderReference =
        selectedOrder?.order_reference;

      if (
        !orderReference
        || selectedOrder?.status !== "draft"
        || approvalInFlightRef.current
      ) {
        return;
      }

      approvalInFlightRef.current = true;
      setApproving(true);
      setApprovalErrorMessage("");
      setApprovedOrder(null);

      try {
        const payload = await approvePurchaseOrder(
          orderReference,
          {},
          {
            companyUuid: selectedCompanyUuid,
          },
        );

        setApprovedOrder(
          payload?.purchase_order || {
            order_reference: orderReference,
            status: "ready_to_dispatch",
          },
        );

        await loadOrders({
          preserveData: true,
        });
      } catch (error) {
        setApprovalErrorMessage(
          getPurchaseOrdersErrorMessage(
            error,
            "Impossible d’approuver la commande fournisseur.",
          ),
        );
      } finally {
        approvalInFlightRef.current = false;
        setApproving(false);
      }
    },
    [
      loadOrders,
      selectedCompanyUuid,
      selectedOrder,
    ],
  );




  const openDispatchForm = useCallback(
    () => {
      if (
        selectedOrder?.status
        !== "ready_to_dispatch"
        || dispatchInFlightRef.current
      ) {
        return;
      }

      setDispatchFormOpen(true);
      setDispatchErrorMessage("");
      setDispatchResult(null);
    },
    [
      selectedOrder?.status,
    ],
  );


  const closeDispatchForm = useCallback(
    () => {
      if (dispatchInFlightRef.current) {
        return;
      }

      setDispatchFormOpen(false);
      setDispatchErrorMessage("");
    },
    [],
  );


  const changeDispatchConnector = useCallback(
    (event) => {
      const connectorKey = event.target.value;
      const connector = dispatchConnectorContract(
        connectorKey,
      );

      setDispatchConnectorKey(connectorKey);
      setDispatchFormatKey(
        connector.defaultFormat,
      );
      setDispatchDestination("");
      setDispatchErrorMessage("");
    },
    [],
  );


  const dispatchSelectedOrder = useCallback(
    async (event) => {
      event.preventDefault();

      const orderReference =
        selectedOrder?.order_reference;

      const destination =
        dispatchDestination.trim();

      if (
        !orderReference
        || selectedOrder?.status
          !== "ready_to_dispatch"
        || dispatchInFlightRef.current
      ) {
        return;
      }

      if (
        activeDispatchConnector
          .destinationRequired
        && !destination
      ) {
        setDispatchErrorMessage(
          "La destination est obligatoire pour ce canal.",
        );
        return;
      }

      dispatchInFlightRef.current = true;
      setDispatching(true);
      setDispatchErrorMessage("");
      setDispatchResult(null);

      try {
        const payload = await dispatchPurchaseOrder(
          orderReference,
          {
            connectorKey: dispatchConnectorKey,
            formatKey: dispatchFormatKey,
            destination: destination || undefined,
            idempotencyKey:
              createDispatchIdempotencyKey(
                orderReference,
                dispatchConnectorKey,
              ),
            attemptNumber: 1,
          },
          {
            companyUuid: selectedCompanyUuid,
          },
        );

        setDispatchResult({
          purchaseOrder:
            payload?.purchase_order || {
              order_reference: orderReference,
              status: "dispatched",
            },
          dispatch: payload?.dispatch || null,
        });

        setDispatchFormOpen(false);

        await loadOrders({
          preserveData: true,
        });
      } catch (error) {
        setDispatchErrorMessage(
          getPurchaseOrdersErrorMessage(
            error,
            "Impossible d’envoyer la commande fournisseur.",
          ),
        );
      } finally {
        dispatchInFlightRef.current = false;
        setDispatching(false);
      }
    },
    [
      activeDispatchConnector,
      dispatchConnectorKey,
      dispatchDestination,
      dispatchFormatKey,
      loadOrders,
      selectedCompanyUuid,
      selectedOrder,
    ],
  );




  const openExportForm = useCallback(
    () => {
      if (
        !selectedOrder?.order_reference
        || exportInFlightRef.current
      ) {
        return;
      }

      setExportFormOpen(true);
      setExportErrorMessage("");
      setExportSuccess(null);
    },
    [
      selectedOrder?.order_reference,
    ],
  );


  const closeExportForm = useCallback(
    () => {
      if (exportInFlightRef.current) {
        return;
      }

      setExportFormOpen(false);
      setExportErrorMessage("");
    },
    [],
  );


  const exportSelectedOrder = useCallback(
    async (event) => {
      event.preventDefault();

      const orderReference =
        selectedOrder?.order_reference;

      if (
        !orderReference
        || exportInFlightRef.current
      ) {
        return;
      }

      exportInFlightRef.current = true;
      setExporting(true);
      setExportErrorMessage("");
      setExportSuccess(null);

      try {
        const result = await exportPurchaseOrder(
          orderReference,
          exportFormatKey,
          {
            companyUuid: selectedCompanyUuid,
          },
        );

        const filename =
          filenameFromContentDisposition(
            result.contentDisposition,
          )
          || purchaseOrderExportFilename(
            orderReference,
            exportFormatKey,
          );

        downloadBlob(
          result.blob,
          filename,
        );

        setExportSuccess({
          orderReference,
          formatKey: exportFormatKey,
          filename,
          checksum: result.checksum,
        });

        setExportFormOpen(false);
      } catch (error) {
        setExportErrorMessage(
          getPurchaseOrdersErrorMessage(
            error,
            "Impossible d’exporter la commande fournisseur.",
          ),
        );
      } finally {
        exportInFlightRef.current = false;
        setExporting(false);
      }
    },
    [
      exportFormatKey,
      selectedCompanyUuid,
      selectedOrder,
    ],
  );


  usePanelActions(
    useMemo(
      () => [
        {
          key: "refresh-purchase-orders",
          label: refreshing
            ? "Actualisation en cours"
            : "Actualiser les commandes",
          icon: "↻",
          onClick: refresh,
          disabled:
            refreshing
            || approving
            || dispatching
            || exporting
            || status
              === PURCHASE_ORDERS_ADMIN_PANEL_STATUS.LOADING,
        },
        ...(
          selectedOrder?.status === "draft"
            ? [
              {
                key: "approve-purchase-order",
                label: approving
                  ? "Approbation en cours"
                  : "Approuver la commande",
                icon: "✓",
                onClick: approveSelectedOrder,
                disabled:
                  approving
                  || refreshing
                  || dispatching
                  || exporting,
              },
            ]
            : []
        ),
        ...(
          selectedOrder?.status
            === "ready_to_dispatch"
            ? [
              {
                key: "dispatch-purchase-order",
                label: dispatching
                  ? "Envoi en cours"
                  : "Envoyer la commande",
                icon: "➜",
                onClick: openDispatchForm,
                disabled:
                  dispatching
                  || approving
                  || refreshing
                  || exporting,
              },
            ]
            : []
        ),
        ...(
          selectedOrder
            ? [
              {
                key: "export-purchase-order",
                label: exporting
                  ? "Export en cours"
                  : "Exporter la commande",
                icon: "⇩",
                onClick: openExportForm,
                disabled:
                  exporting
                  || approving
                  || dispatching
                  || refreshing,
              },
            ]
            : []
        ),
      ],
      [
        approveSelectedOrder,
        approving,
        dispatching,
        exporting,
        openDispatchForm,
        openExportForm,
        refresh,
        refreshing,
        selectedOrder?.status,
        status,
      ],
    ),
  );


  return (
    <PanelRoot>
      {status
        === PURCHASE_ORDERS_ADMIN_PANEL_STATUS.LOADING && (
        <AdminLoadingState
          message="Chargement des commandes fournisseurs..."
        />
      )}

      {status
        === PURCHASE_ORDERS_ADMIN_PANEL_STATUS.ERROR && (
        <AdminErrorState
          message={errorMessage}
          compact
        />
      )}

      {status
        === PURCHASE_ORDERS_ADMIN_PANEL_STATUS.EMPTY && (
        <AdminEmptyState
          message="Aucune commande fournisseur n’est disponible."
        />
      )}

      {status
        === PURCHASE_ORDERS_ADMIN_PANEL_STATUS.READY && (
        <>
          <Summary>
            <strong>{orders.length}</strong>{" "}
            commande
            {orders.length > 1 ? "s" : ""} fournisseur
            {orders.length > 1 ? "s" : ""}
            {refreshing ? " — actualisation…" : ""}
          </Summary>

          <ApprovalFeedback
            aria-live="polite"
            aria-atomic="true"
          >
            {approving && (
              <AdminLoadingState
                message="Approbation de la commande fournisseur..."
                compact
              />
            )}

            {!approving
              && approvalErrorMessage && (
              <AdminErrorState
                message={approvalErrorMessage}
                compact
              />
            )}

            {!approving
              && approvedOrder && (
              <AdminSuccessState
                data-testid="purchase-order-approval-success"
              >
                <strong>
                  Commande fournisseur approuvée
                </strong>

                <span>
                  La commande{" "}
                  <b>
                    {displayValue(
                      approvedOrder.order_reference,
                    )}
                  </b>{" "}
                  est désormais au statut{" "}
                  <b>
                    {statusLabel(
                      approvedOrder.status,
                    )}
                  </b>.
                </span>
              </AdminSuccessState>
            )}
          </ApprovalFeedback>


          <DispatchFeedback
            aria-live="polite"
            aria-atomic="true"
          >
            {dispatching && (
              <AdminLoadingState
                message="Envoi de la commande fournisseur..."
                compact
              />
            )}

            {!dispatching
              && dispatchErrorMessage && (
              <AdminErrorState
                message={dispatchErrorMessage}
                compact
              />
            )}

            {!dispatching
              && dispatchResult && (
              <DispatchSuccess
                role="status"
                data-testid="purchase-order-dispatch-success"
              >
                <strong>
                  Commande fournisseur envoyée
                </strong>

                <span>
                  La commande{" "}
                  <b>
                    {displayValue(
                      dispatchResult
                        .purchaseOrder
                        ?.order_reference,
                    )}
                  </b>{" "}
                  est désormais au statut{" "}
                  <b>
                    {statusLabel(
                      dispatchResult
                        .purchaseOrder
                        ?.status,
                    )}
                  </b>.
                </span>

                {dispatchResult
                  .dispatch
                  ?.response_message && (
                  <small>
                    {
                      dispatchResult
                        .dispatch
                        .response_message
                    }
                  </small>
                )}
              </DispatchSuccess>
            )}
          </DispatchFeedback>


          <ExportFeedback
            aria-live="polite"
            aria-atomic="true"
          >
            {exporting && (
              <AdminLoadingState
                message="Export de la commande fournisseur..."
                compact
              />
            )}

            {!exporting
              && exportErrorMessage && (
              <AdminErrorState
                message={exportErrorMessage}
                compact
              />
            )}

            {!exporting
              && exportSuccess && (
              <ExportSuccess
                role="status"
                data-testid="purchase-order-export-success"
              >
                <strong>
                  Commande fournisseur exportée
                </strong>

                <span>
                  Le fichier{" "}
                  <b>{exportSuccess.filename}</b>{" "}
                  a été généré au format{" "}
                  <b>
                    {
                      PURCHASE_ORDER_FORMAT_LABELS[
                        exportSuccess.formatKey
                      ]
                    }
                  </b>.
                </span>

                {exportSuccess.checksum && (
                  <small>
                    Checksum : {exportSuccess.checksum}
                  </small>
                )}
              </ExportSuccess>
            )}
          </ExportFeedback>

          <TableViewport>
            <OrdersTable>
              <thead>
                <tr>
                  <th scope="col">Commande</th>
                  <th scope="col">Fournisseur</th>
                  <th scope="col">Statut</th>
                  <th scope="col">Lignes</th>
                  <th scope="col">Quantité</th>
                  <th scope="col">Total HT</th>
                  <th scope="col">Détail</th>
                </tr>
              </thead>

              <tbody>
                {orders.map((order, index) => {
                  const reference =
                    displayValue(order?.order_reference);
                  const isSelected =
                    order?.order_reference
                    === selectedOrderReference;

                  return (
                    <tr key={orderKey(order, index)}>
                      <td>
                        <strong>{reference}</strong>
                      </td>

                      <td>
                        {displayValue(order?.supplier_uuid)}
                      </td>

                      <td>
                        <StatusBadge $status={order?.status}>
                          {statusLabel(order?.status)}
                        </StatusBadge>
                      </td>

                      <td>
                        {orderLines(order).length}
                      </td>

                      <td>
                        {formatQuantity(
                          orderQuantity(order),
                        )}
                      </td>

                      <td>
                        {formatAmount(
                          order?.total_ht,
                          order?.currency,
                        )}
                      </td>

                      <td>
                        <AdminButton
                          type="button"
                          variant={
                            isSelected
                              ? "primary"
                              : "secondary"
                          }
                          aria-pressed={isSelected}
                          aria-label={
                            `Consulter la commande ${reference}`
                          }
                          onClick={() => {
                            setSelectedOrderReference(
                              order?.order_reference || "",
                            );
                            setDispatchFormOpen(false);
                            setDispatchErrorMessage("");
                            setDispatchResult(null);
                            setExportFormOpen(false);
                            setExportErrorMessage("");
                            setExportSuccess(null);
                          }}
                        >
                          {isSelected
                            ? "Sélectionnée"
                            : "Consulter"}
                        </AdminButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </OrdersTable>
          </TableViewport>

          {selectedOrder && (
            <OrderDetail
              aria-label={
                `Détail de la commande ${
                  selectedOrder.order_reference
                }`
              }
            >
              <DetailHeader>
                <div>
                  <DetailLabel>
                    Commande sélectionnée
                  </DetailLabel>

                  <DetailTitle>
                    {selectedOrder.order_reference}
                  </DetailTitle>
                </div>

                <DetailTotal>
                  <span>Total HT</span>
                  <strong>
                    {formatAmount(
                      selectedOrder.total_ht,
                      selectedOrder.currency,
                    )}
                  </strong>
                </DetailTotal>
              </DetailHeader>

              <DetailMetadata>
                <span>
                  Fournisseur :{" "}
                  <strong>
                    {displayValue(
                      selectedOrder.supplier_uuid,
                    )}
                  </strong>
                </span>

                <span>
                  Statut :{" "}
                  <strong>
                    {statusLabel(
                      selectedOrder.status,
                    )}
                  </strong>
                </span>

                <span>
                  Devise :{" "}
                  <strong>
                    {displayValue(
                      selectedOrder.currency,
                      "EUR",
                    )}
                  </strong>
                </span>
              </DetailMetadata>




              {exportFormOpen && (
                <ExportForm
                  onSubmit={exportSelectedOrder}
                  aria-label={
                    `Exporter la commande ${
                      selectedOrder.order_reference
                    }`
                  }
                >
                  <DispatchFormHeader>
                    <div>
                      <strong>
                        Export local
                      </strong>

                      <span>
                        Le téléchargement ne modifie pas
                        la commande.
                      </span>
                    </div>

                    <AdminButton
                      type="button"
                      variant="secondary"
                      onClick={closeExportForm}
                      disabled={exporting}
                    >
                      Fermer
                    </AdminButton>
                  </DispatchFormHeader>

                  <ExportFields>
                    <DispatchField>
                      <span>Format</span>

                      <select
                        aria-label="Format d’export"
                        value={exportFormatKey}
                        onChange={(event) => {
                          setExportFormatKey(
                            event.target.value,
                          );
                          setExportErrorMessage("");
                        }}
                        disabled={exporting}
                      >
                        {Object.entries(
                          PURCHASE_ORDER_FORMAT_LABELS,
                        ).map(
                          ([
                            formatKey,
                            formatLabel,
                          ]) => (
                            <option
                              key={formatKey}
                              value={formatKey}
                            >
                              {formatLabel}
                            </option>
                          ),
                        )}
                      </select>
                    </DispatchField>
                  </ExportFields>

                  <DispatchActions>
                    <AdminButton
                      type="submit"
                      variant="primary"
                      disabled={exporting}
                    >
                      {exporting
                        ? "Export en cours…"
                        : "Télécharger"}
                    </AdminButton>
                  </DispatchActions>
                </ExportForm>
              )}

              {dispatchFormOpen
                && selectedOrder.status
                  === "ready_to_dispatch" && (
                <DispatchForm
                  onSubmit={dispatchSelectedOrder}
                  aria-label={
                    `Envoyer la commande ${
                      selectedOrder.order_reference
                    }`
                  }
                >
                  <DispatchFormHeader>
                    <div>
                      <strong>
                        Envoi fournisseur
                      </strong>

                      <span>
                        Sélectionnez le canal et le format.
                      </span>
                    </div>

                    <AdminButton
                      type="button"
                      variant="secondary"
                      onClick={closeDispatchForm}
                      disabled={dispatching}
                    >
                      Fermer
                    </AdminButton>
                  </DispatchFormHeader>

                  <DispatchFields>
                    <DispatchField>
                      <span>Canal</span>

                      <select
                        aria-label="Canal d’envoi"
                        value={dispatchConnectorKey}
                        onChange={
                          changeDispatchConnector
                        }
                        disabled={dispatching}
                      >
                        {Object.entries(
                          PURCHASE_ORDER_DISPATCH_CONNECTORS,
                        ).map(
                          ([
                            connectorKey,
                            connector,
                          ]) => (
                            <option
                              key={connectorKey}
                              value={connectorKey}
                            >
                              {connector.label}
                            </option>
                          ),
                        )}
                      </select>
                    </DispatchField>

                    <DispatchField>
                      <span>Format</span>

                      <select
                        aria-label="Format d’envoi"
                        value={dispatchFormatKey}
                        onChange={(event) => {
                          setDispatchFormatKey(
                            event.target.value,
                          );
                          setDispatchErrorMessage("");
                        }}
                        disabled={dispatching}
                      >
                        {availableDispatchFormats.map(
                          (formatKey) => (
                            <option
                              key={formatKey}
                              value={formatKey}
                            >
                              {
                                PURCHASE_ORDER_FORMAT_LABELS[
                                  formatKey
                                ]
                              }
                            </option>
                          ),
                        )}
                      </select>
                    </DispatchField>

                    <DispatchField>
                      <span>
                        {
                          activeDispatchConnector
                            .destinationLabel
                        }
                        {
                          activeDispatchConnector
                            .destinationRequired
                            ? " *"
                            : ""
                        }
                      </span>

                      <input
                        aria-label={
                          activeDispatchConnector
                            .destinationLabel
                        }
                        value={dispatchDestination}
                        onChange={(event) => {
                          setDispatchDestination(
                            event.target.value,
                          );
                          setDispatchErrorMessage("");
                        }}
                        required={
                          activeDispatchConnector
                            .destinationRequired
                        }
                        disabled={dispatching}
                        placeholder={
                          dispatchConnectorKey === "email"
                            ? "commandes@fournisseur.fr"
                            : dispatchConnectorKey
                              === "sftp"
                              ? "sftp://fournisseur/commandes"
                              : "Optionnel"
                        }
                      />
                    </DispatchField>
                  </DispatchFields>

                  <DispatchActions>
                    <AdminButton
                      type="submit"
                      variant="primary"
                      disabled={dispatching}
                    >
                      {dispatching
                        ? "Envoi en cours…"
                        : "Confirmer l’envoi"}
                    </AdminButton>
                  </DispatchActions>
                </DispatchForm>
              )}

              <LineTableViewport>
                <LinesTable>
                  <thead>
                    <tr>
                      <th scope="col">Référence distributeur</th>
                      <th scope="col">Désignation</th>
                      <th scope="col">Quantité</th>
                      <th scope="col">Gratuit</th>
                      <th scope="col">PU HT</th>
                      <th scope="col">Total HT</th>
                    </tr>
                  </thead>

                  <tbody>
                    {orderLines(selectedOrder).map(
                      (line, index) => (
                        <tr key={lineKey(line, index)}>
                          <td>
                            <strong>
                              {displayValue(
                                line?.supplier_reference,
                              )}
                            </strong>

                            {line?.line_reference && (
                              <LineReference>
                                {line.line_reference}
                              </LineReference>
                            )}
                          </td>

                          <td>
                            {displayValue(
                              line?.designation,
                            )}
                          </td>

                          <td>
                            {formatQuantity(
                              line?.quantity,
                            )}
                          </td>

                          <td>
                            {formatQuantity(
                              line?.free_quantity,
                            )}
                          </td>

                          <td>
                            {formatAmount(
                              line?.unit_price_ht,
                              selectedOrder.currency,
                            )}
                          </td>

                          <td>
                            {formatAmount(
                              line?.total_ht,
                              selectedOrder.currency,
                            )}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </LinesTable>
              </LineTableViewport>

              {!orderLines(selectedOrder).length && (
                <NoLines>
                  Cette commande ne contient aucune ligne.
                </NoLines>
              )}
            </OrderDetail>
          )}
        </>
      )}
    </PanelRoot>
  );
}


const PanelRoot = styled.section`
  min-width: 0;
  color: #f7f7f7;
`;


const Summary = styled.p`
  margin: 0 0 0.75rem;
  color: rgba(247, 247, 247, 0.68);
`;


const ApprovalFeedback = styled.div`
  display: grid;
  gap: 0.75rem;
  margin-bottom: 1rem;

  &:empty {
    display: none;
  }
`;


const ExportFeedback = styled.div`
  display: grid;
  gap: 0.75rem;
  margin-bottom: 1rem;

  &:empty {
    display: none;
  }
`;


const ExportSuccess = styled.div`
  display: grid;
  gap: 0.35rem;
  padding: 0.85rem 1rem;
  border:
    1px solid rgba(126, 183, 255, 0.28);
  border-radius: 14px;
  background: rgba(83, 155, 255, 0.12);
  color: #d4e6ff;

  strong {
    color: #edf5ff;
  }

  span,
  small {
    color: rgba(237, 245, 255, 0.82);
  }
`;


const ExportForm = styled.form`
  display: grid;
  gap: 1rem;
  margin: 1rem 0;
  padding: 1rem;
  border:
    1px solid rgba(126, 183, 255, 0.24);
  border-radius: 16px;
  background: rgba(16, 27, 48, 0.72);
`;


const ExportFields = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 320px);
  gap: 0.85rem;
`;


const DispatchFeedback = styled.div`
  display: grid;
  gap: 0.75rem;
  margin-bottom: 1rem;

  &:empty {
    display: none;
  }
`;


const DispatchSuccess = styled.div`
  display: grid;
  gap: 0.35rem;
  padding: 0.85rem 1rem;
  border:
    1px solid rgba(83, 155, 255, 0.3);
  border-radius: 14px;
  background: rgba(83, 155, 255, 0.12);
  color: #d4e6ff;

  strong {
    color: #edf5ff;
  }

  span,
  small {
    color: rgba(237, 245, 255, 0.82);
  }
`;


const DispatchForm = styled.form`
  display: grid;
  gap: 1rem;
  margin: 1rem 0;
  padding: 1rem;
  border:
    1px solid rgba(83, 155, 255, 0.24);
  border-radius: 16px;
  background: rgba(16, 27, 48, 0.72);
`;


const DispatchFormHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;

  div {
    display: grid;
    gap: 0.25rem;
  }

  span {
    color: rgba(247, 247, 247, 0.66);
  }
`;


const DispatchFields = styled.div`
  display: grid;
  grid-template-columns:
    minmax(160px, 0.8fr)
    minmax(140px, 0.6fr)
    minmax(240px, 1.6fr);
  gap: 0.85rem;

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;


const DispatchField = styled.label`
  display: grid;
  gap: 0.4rem;

  > span {
    color: rgba(247, 247, 247, 0.7);
    font-size: 0.8rem;
    font-weight: 750;
  }

  select,
  input {
    min-width: 0;
    padding: 0.72rem 0.8rem;
    border:
      1px solid rgba(255, 255, 255, 0.14);
    border-radius: 12px;
    background: #172138;
    color: #f7f7f7;
    font: inherit;
  }

  select:focus,
  input:focus {
    outline:
      2px solid rgba(83, 155, 255, 0.55);
    outline-offset: 1px;
  }
`;


const DispatchActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;


const TableViewport = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
`;


const OrdersTable = styled.table`
  width: 100%;
  min-width: 960px;
  border-collapse: collapse;
  background: #202a43;

  th,
  td {
    padding: 0.85rem;
    border-bottom:
      1px solid rgba(255, 255, 255, 0.07);
    text-align: left;
    vertical-align: middle;
  }

  th {
    color: rgba(247, 247, 247, 0.62);
    font-size: 0.76rem;
    font-weight: 850;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;


const StatusBadge = styled.span`
  display: inline-flex;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  background:
    ${({ $status }) => {
      if (
        $status === "dispatch_failed"
        || $status === "rejected"
        || $status === "cancelled"
      ) {
        return "rgba(225, 95, 95, 0.16)";
      }

      if (
        $status === "dispatched"
        || $status === "acknowledged"
        || $status === "received"
        || $status === "closed"
      ) {
        return "rgba(73, 184, 126, 0.16)";
      }

      return "rgba(255, 255, 255, 0.08)";
    }};
  color:
    ${({ $status }) => {
      if (
        $status === "dispatch_failed"
        || $status === "rejected"
        || $status === "cancelled"
      ) {
        return "#ffaaaa";
      }

      if (
        $status === "dispatched"
        || $status === "acknowledged"
        || $status === "received"
        || $status === "closed"
      ) {
        return "#8ce0b2";
      }

      return "rgba(247, 247, 247, 0.75)";
    }};
  font-size: 0.75rem;
  font-weight: 850;
`;


const OrderDetail = styled.section`
  margin-top: 1.25rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.035);
`;


const DetailHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;


const DetailLabel = styled.span`
  color: rgba(247, 247, 247, 0.58);
  font-size: 0.78rem;
`;


const DetailTitle = styled.h3`
  margin: 0.2rem 0 0;
  font-size: 1.05rem;
`;


const DetailTotal = styled.div`
  display: grid;
  gap: 0.15rem;
  text-align: right;

  span {
    color: rgba(247, 247, 247, 0.58);
    font-size: 0.78rem;
  }
`;


const DetailMetadata = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
  margin-bottom: 1rem;
  color: rgba(247, 247, 247, 0.68);
  font-size: 0.84rem;
`;


const LineTableViewport = styled.div`
  width: 100%;
  overflow-x: auto;
`;


const LinesTable = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;

  th,
  td {
    padding: 0.7rem;
    border-bottom:
      1px solid rgba(255, 255, 255, 0.07);
    text-align: left;
    vertical-align: top;
  }

  th {
    color: rgba(247, 247, 247, 0.58);
    font-size: 0.72rem;
    text-transform: uppercase;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;


const LineReference = styled.span`
  display: block;
  margin-top: 0.2rem;
  color: rgba(247, 247, 247, 0.48);
  font-size: 0.74rem;
`;


const NoLines = styled.p`
  margin: 0.75rem 0 0;
  color: rgba(247, 247, 247, 0.62);
`;
